import React, { useCallback, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import NoPiiNotice from 'src/common/ui/NoPiiNotice';
import { ChangeControlSeverity } from 'src/generated/graphql';
import { formatDayMonthYearTimeUtc } from 'src/common/utils/dateFormatters';
import { ActiveChangeControlWindow } from 'src/common/ui/ChangeControlBanner/useChangeControlStatus';
import {
  MCM_ID_DESCRIPTION_KEY,
  MCM_ID_PLACEHOLDER,
  WINDOW_LABEL_KEY,
  mcmOverrideErrorKey,
} from './changeControlOverride';
import {
  NOTIFICATION_EMAIL_PLACEHOLDER,
  notificationEmailErrorKey,
} from './notificationEmail';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface SubmitTranslationModalProps {
  visible: boolean;
  loading: boolean;
  onDismiss: () => void;
  /**
   * @param notificationEmail the validated, trimmed address
   * @param mcmId the trimmed MCM id the operator cited as a change-control
   *   override, or `undefined` when the field was not collected. Only ever
   *   non-undefined while the MCM field is rendered — an active window AND
   *   `editsLiveStrings` — because it is not collected otherwise.
   */
  onConfirm: (notificationEmail: string, mcmId?: string) => void;
  clearDraft: () => void;
  shouldClearDraft: boolean;
  /**
   * The active Restricted/Blocked (peak-freeze) window, or `null`/absent on a
   * normal day. When present the modal explains the freeze and offers the
   * MCM field. Read by the page from `useChangeControlStatus`, which
   * fails open — so on an outage this is `null` and the modal behaves exactly
   * as on a normal day, leaving the backend as the authoritative gate.
   */
  changeControlWindow?: ActiveChangeControlWindow | null;
  /**
   * Whether the draft changes text that is already live. Inside a window that is
   * what makes an MCM required (design §8.3): a submission that only adds new
   * strings, or only adds new CX variants to existing strings, is exempt and the
   * MCM field is not shown at all — nothing needs authorising, so there is
   * nothing to collect. Ignored when no window is active. Required rather than
   * defaulted: a forgotten `false` would hide the field on a freeze day and
   * send the operator into a backend `OVERRIDE_REQUIRED` refusal.
   */
  editsLiveStrings: boolean;
}

/**
 * Confirmation modal that collects a notification email before submitting
 * a string group for translation. Inside a change-control window it also
 * explains the freeze and collects the MCM the operator cites as an override.
 */
const SubmitTranslationModal: React.FC<SubmitTranslationModalProps> = ({
  visible,
  loading,
  onDismiss,
  onConfirm,
  shouldClearDraft,
  clearDraft,
  changeControlWindow = null,
  editsLiveStrings,
}) => {
  const { t, locale } = useTranslation();
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [mcmId, setMcmId] = useState('');
  const [overrideTouched, setOverrideTouched] = useState(false);

  const trimmedEmail = email.trim();
  const emailErrorKey = notificationEmailErrorKey(trimmedEmail);
  const emailErrorText = emailErrorKey ? t(emailErrorKey) : undefined;
  const isEmailValid = emailErrorText === undefined;
  const showEmailError = touched && !isEmailValid;

  // The MCM field exists only inside a window AND only when the draft edits or
  // deletes live strings — the case the freeze is about. An exempt submission
  // (new strings / new variants only) has nothing to authorise, so the field is
  // not rendered rather than offered as optional: a cited-but-unneeded MCM would
  // only be ignored by the gate. When shown, the field is required and a
  // non-blank id must at least look like an MCM id before it is sent.
  // One source of truth for "collect an override": the window that applies to
  // this submission, or null. The alert and the MCM field both key off it, so
  // they cannot show one without the other.
  const activeOverrideWindow =
    changeControlWindow !== null && editsLiveStrings
      ? changeControlWindow
      : null;
  const overrideFieldVisible = activeOverrideWindow !== null;
  const trimmedMcmId = mcmId.trim();
  const overrideErrorKey = mcmOverrideErrorKey(
    overrideFieldVisible,
    trimmedMcmId,
  );
  const overrideErrorText = overrideErrorKey ? t(overrideErrorKey) : undefined;
  const isOverrideValid = overrideErrorKey === undefined;
  const showOverrideError = overrideTouched && !isOverrideValid;

  const isValid = isEmailValid && isOverrideValid;

  const reset = useCallback(() => {
    setEmail('');
    setTouched(false);
    setMcmId('');
    setOverrideTouched(false);
  }, []);

  const handleConfirm = useCallback(() => {
    setTouched(true);
    setOverrideTouched(true);
    if (!isValid) {
      return;
    }
    // `undefined` when the field is hidden, so a normal-day submit is
    // byte-identical to what it was before the field existed. When visible,
    // `isValid` already guarantees a non-blank, well-formed id.
    onConfirm(trimmedEmail, overrideFieldVisible ? trimmedMcmId : undefined);
    // The parent hides this modal without unmounting it, so clear the fields
    // here too — otherwise the next open shows the previous values and errors.
    reset();
  }, [
    isValid,
    onConfirm,
    trimmedEmail,
    overrideFieldVisible,
    trimmedMcmId,
    reset,
  ]);

  const handleDismiss = useCallback(
    (shouldClearDraft: boolean) => {
      if (shouldClearDraft) {
        clearDraft();
      }
      reset();
      onDismiss();
    },
    [onDismiss, clearDraft, reset],
  );

  // Single dismiss handler shared by the Modal's onDismiss (Esc / overlay / X)
  // and the Cancel button, so both close paths behave identically.
  const handleDismissClick = useCallback(
    () => handleDismiss(shouldClearDraft),
    [handleDismiss, shouldClearDraft],
  );

  return (
    <Modal
      visible={visible}
      // Guard the Esc / overlay / X dismiss with `loading`, matching the Cancel
      // button's `disabled={loading}`: dismissing now has a side effect
      // (clearDraft when shouldClearDraft), so allowing it mid-submit could
      // discard the just-made group edit while the submission is in flight.
      onDismiss={loading ? undefined : handleDismissClick}
      header={t('sgd-submit-title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismissClick} disabled={loading}>
              {t('string-cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={loading}
              disabled={!email.trim()}
            >
              {t('sgd-submit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <NoPiiNotice />
        {activeOverrideWindow && (
          <Alert
            type={
              activeOverrideWindow.severity === ChangeControlSeverity.Blocked
                ? 'error'
                : 'warning'
            }
            statusIconAriaLabel={
              activeOverrideWindow.severity === ChangeControlSeverity.Blocked
                ? t('status-icon-error')
                : t('status-icon-warning')
            }
            header={t(WINDOW_LABEL_KEY[activeOverrideWindow.severity])}
          >
            {/* `reason` is the freeze name from the change-control calendar and
                is passed through untranslated; the surrounding sentence is a
                single resource so each locale controls its own word order. */}
            {t('sgd-cc-window-submit-notice', {
              reason: activeOverrideWindow.reason,
              until: formatDayMonthYearTimeUtc(
                locale,
                new Date(activeOverrideWindow.windowEnd),
              ),
            })}
          </Alert>
        )}
        <FormField
          label={t('sgd-notification-email')}
          description={t('sgd-notification-email-help')}
          errorText={showEmailError ? emailErrorText : undefined}
        >
          <Input
            value={email}
            onChange={({ detail }) => setEmail(detail.value)}
            onBlur={() => setTouched(true)}
            placeholder={NOTIFICATION_EMAIL_PLACEHOLDER}
            type="email"
            ariaRequired={true}
          />
        </FormField>
        {overrideFieldVisible && (
          <FormField
            label={t('sgd-mcm-id')}
            description={t(MCM_ID_DESCRIPTION_KEY)}
            errorText={showOverrideError ? overrideErrorText : undefined}
          >
            <Input
              value={mcmId}
              onChange={({ detail }) => setMcmId(detail.value)}
              onBlur={() => setOverrideTouched(true)}
              placeholder={MCM_ID_PLACEHOLDER}
              ariaRequired={true}
            />
          </FormField>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default SubmitTranslationModal;
